import type {
  GlobalAssistantDiscoveryKind,
  GlobalAssistantSuggestMatch,
} from './global-ai-assistant-api';

const DATA_DICTIONARY_PATH = '/data-dictionary';

/**
 * Pages that hold no filters at all, so a prompt on one of these matches must
 * never start a route-filter resolution — there is nothing for the resolved
 * filters to land on, and resolving costs the user a multi-second round trip
 * before a navigation that ignores it.
 *
 * Two kinds qualify: the iframed Data Dictionary (the page IS the answer to a
 * definitional question), and the enrichment applications (their subject is the
 * user's own uploaded file, not a filtered population).
 */
const UNFILTERABLE_PATHS: ReadonlySet<string> = new Set([
  DATA_DICTIONARY_PATH,
  '/deliverables/data-enrichment',
  '/resume-enrichment',
]);

export type GlobalAssistantPageMatchAction =
  | { kind: 'navigate_only' }
  | { kind: 'resolve_route_filters' }
  | {
      kind: 'apply_discovery_filters_after_navigate';
      discoveryKind: GlobalAssistantDiscoveryKind;
    };

export function getGlobalAssistantPageMatchAction(
  match: GlobalAssistantSuggestMatch,
  prompt?: string
): GlobalAssistantPageMatchAction {
  const hasPrompt = Boolean(prompt?.trim());
  if (match.discoveryKind) {
    if (match.applyFiltersAfterNavigate && hasPrompt) {
      return {
        kind: 'apply_discovery_filters_after_navigate',
        discoveryKind: match.discoveryKind,
      };
    }
    return { kind: 'navigate_only' };
  }
  if (UNFILTERABLE_PATHS.has(match.navigatePath)) {
    return { kind: 'navigate_only' };
  }
  if (hasPrompt) {
    return { kind: 'resolve_route_filters' };
  }
  return { kind: 'navigate_only' };
}

export function getGlobalAssistantPageMatchesDefaultMessage({
  hasCurrentPageFilterApplyOption,
  matches,
}: {
  hasCurrentPageFilterApplyOption: boolean;
  matches: GlobalAssistantSuggestMatch[];
}): string {
  if (
    matches.length > 0 &&
    matches.every((match) => match.navigatePath === DATA_DICTIONARY_PATH)
  ) {
    return 'Open the Data Dictionary for methodology and metric definitions.';
  }
  if (hasCurrentPageFilterApplyOption) {
    return 'I can apply these filters to the current page:';
  }
  const hasFilterApplyIntent = matches.some((match) => {
    if (UNFILTERABLE_PATHS.has(match.navigatePath)) {
      return false;
    }
    if (match.applyFiltersAfterNavigate) {
      return true;
    }
    return Boolean(match.resolvedPromptForApply?.trim());
  });
  if (!hasFilterApplyIntent) {
    if (matches.length === 1) {
      return 'You can open the page below:';
    }
    return 'You can open one of the following pages:';
  }
  if (matches.length === 1) {
    return 'I can apply these filters to the page below:';
  }
  return 'I can apply these filters to one of the following pages:';
}
